import type { BuildContext, Fragment } from "./types.js";
import { dimensionsFor } from "./format.js";
import { colors } from "../brand/theme.js";

/**
 * Returns the `<head>` block with shared CSS resets, Google Fonts loader, and
 * the runtime tweaks we always want: full-bleed body, no scrollbars, sans-serif
 * fallback consistent with Remotion's behavior.
 */
function buildHead(opts: {
  title: string;
  compositionId: string;
  fontsCss: string;
  customCss: string;
  width: number;
  height: number;
  durationSec: number;
}): string {
  return `<!doctype html>
<html lang="de" data-composition-id="${escapeHtml(opts.compositionId)}" data-composition-duration="${Number(opts.durationSec.toFixed(4))}">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(opts.title)}</title>
    ${opts.fontsCss}
    <style>
      html, body { margin: 0; padding: 0; background: ${colors.bg}; overflow: hidden; }
      body { font-family: "Outfit", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif; -webkit-font-smoothing: antialiased; }
      #stage { position: relative; width: ${opts.width}px; height: ${opts.height}px; overflow: hidden; color: ${colors.fg}; }
      .clip { will-change: transform, opacity; }
${opts.customCss}
    </style>
  </head>`;
}

export function buildCompositionHtml(opts: {
  compositionId: string;
  title?: string;
  ctx: BuildContext;
  fragments: Fragment[];
  /** Optional extra <head> elements such as <link rel="preload"> for media. */
  extraHead?: string;
}): string {
  const { ctx } = opts;
  const { width, height } = dimensionsFor(ctx.format);
  const merged = mergeFragments(opts.fragments);
  const fontsCss = renderGoogleFontsLink();

  const tweenLines = (merged.tweens ?? []).map((t) => "        " + t).join("\n");
  const initLines = (merged.init ?? "")
    .split("\n")
    .filter((l) => l.trim().length > 0)
    .map((l) => "        " + l)
    .join("\n");

  const compositionId = opts.compositionId;
  const head = buildHead({
    title: opts.title ?? compositionId,
    compositionId,
    fontsCss: fontsCss + (opts.extraHead ?? ""),
    customCss: merged.css ?? "",
    width,
    height,
    durationSec: ctx.durationSec,
  });

  return `${head}
  <body>
    <div id="stage" data-composition-id="${escapeHtml(compositionId)}" data-start="0" data-width="${width}" data-height="${height}">
${indent(merged.html, 6)}
    </div>

    <script>
      // ── DEBUG BRIDGE ────────────────────────────────────────────────────
      // Forward iframe-side errors and console output to the parent frame
      // so the Composer DevTools can show them without having to switch
      // frame context to about:srcdoc manually. Tagged with [hf-iframe] so
      // they're easy to spot.
      (function() {
        if (window.parent === window) return; // standalone (e.g. hyperframes studio)
        function post(level, args) {
          try {
            window.parent.postMessage({
              source: "hf-iframe-log",
              level: level,
              message: Array.prototype.slice.call(args).map(function(a){
                try { return typeof a === "string" ? a : JSON.stringify(a); }
                catch(e) { return String(a); }
              }).join(" "),
            }, "*");
          } catch (e) {}
        }
        ["log","warn","error","info"].forEach(function(lvl){
          var orig = console[lvl].bind(console);
          console[lvl] = function() { post(lvl, arguments); orig.apply(console, arguments); };
        });
        window.addEventListener("error", function(e) {
          post("error", ["[uncaught]", e.message, "@", e.filename + ":" + e.lineno]);
        });
        window.addEventListener("unhandledrejection", function(e) {
          post("error", ["[rejection]", (e.reason && e.reason.message) || String(e.reason)]);
        });
      })();
    </script>
    <script src="https://cdn.jsdelivr.net/npm/gsap@3/dist/gsap.min.js"></script>
    <script>
      (function() {
        if (typeof gsap === "undefined") {
          // GSAP failed to load (offline / CDN blocked). Reveal a fallback so
          // the screen isn't silently empty — the user at least sees that the
          // composition rendered. Real renders (puppeteer producer) always
          // have network, so this only ever fires in the dev composer.
          document.body.style.background = "#1a1a1f";
          var notice = document.createElement("div");
          notice.style.cssText = "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#aaa;font:600 24px Inter,system-ui,sans-serif;text-align:center;padding:40px;";
          notice.textContent = "GSAP konnte nicht geladen werden — Internetverbindung pruefen.";
          document.body.appendChild(notice);
          return;
        }
        const tl = gsap.timeline({ paused: true });
${initLines}
${tweenLines}
        window.__timelines = window.__timelines || {};
        window.__timelines[${JSON.stringify(compositionId)}] = tl;
        // Initial paint: seek to 0 so the timeline's gsap.set() initial
        // states (opacity:0 etc.) are applied to the DOM — without this the
        // frame looks empty until an external driver first calls seek().
        try { tl.seek(0, false); } catch (e) {}
      })();
    </script>
  </body>
</html>
`;
}

function mergeFragments(parts: Fragment[]): Fragment {
  return {
    html: parts.map((p) => p.html).join("\n"),
    css: parts
      .map((p) => p.css)
      .filter((c): c is string => Boolean(c))
      .join("\n"),
    init: parts
      .map((p) => p.init)
      .filter((s): s is string => Boolean(s))
      .join("\n"),
    tweens: parts.flatMap((p) => p.tweens ?? []),
  };
}

function indent(text: string, spaces: number): string {
  const pad = " ".repeat(spaces);
  return text
    .split("\n")
    .map((line) => (line.length ? pad + line : line))
    .join("\n");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Build the <link> tag pulling Outfit + Inter from Google Fonts via the CSS2
 * API. This is the Hyperframes-side equivalent of `@remotion/google-fonts`.
 */
function renderGoogleFontsLink(): string {
  const families = [
    "Outfit:wght@400;500;600;700;800;900",
    "Inter:wght@400;500;600;700;800;900",
  ];
  const href = `https://fonts.googleapis.com/css2?${families
    .map((f) => `family=${encodeURIComponent(f)}`)
    .join("&")}&display=swap`;
  return `<link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link rel="stylesheet" href="${href}" />`;
}

export { escapeHtml, indent };
